import { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import SearchResultsPage from "./pages/SearchResultsPage";
import CityPage from "./pages/CityPage";
import EMICalculatorPage from "./pages/EMICalculatorPage";
import ComparePage from "./pages/ComparePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import BlogPage from "./pages/BlogPage";
import BlogArticlePage from "./pages/BlogArticlePage";
// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProperties from "./pages/admin/AdminProperties";
import AdminPropertyForm from "./pages/admin/AdminPropertyForm";
import AdminInquiries from "./pages/admin/AdminInquiries";
import AdminBlogList from "./pages/admin/AdminBlogList";
import AdminBlogForm from "./pages/admin/AdminBlogForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Compare Context
const CompareContext = createContext(null);

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error("useCompare must be used within CompareProvider");
  }
  return context;
};

// Admin Context
const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within AdminProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Ignore logout errors
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, googleLogin, token, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Compare Provider
const CompareProvider = ({ children }) => {
  const [compareList, setCompareList] = useState(() => {
    const saved = localStorage.getItem("compareList");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("compareList", JSON.stringify(compareList));
  }, [compareList]);

  const addToCompare = (property) => {
    if (compareList.length >= 4) {
      toast.error("Maximum 4 properties can be compared");
      return false;
    }
    if (compareList.find((p) => p.property_id === property.property_id)) {
      toast.info("Property already in compare list");
      return false;
    }
    setCompareList([...compareList, property]);
    toast.success("Added to compare");
    return true;
  };

  const removeFromCompare = (propertyId) => {
    setCompareList(compareList.filter((p) => p.property_id !== propertyId));
    toast.success("Removed from compare");
  };

  const clearCompare = () => {
    setCompareList([]);
  };

  const isInCompare = (propertyId) => {
    return compareList.some((p) => p.property_id === propertyId);
  };

  return (
    <CompareContext.Provider value={{ compareList, addToCompare, removeFromCompare, clearCompare, isInCompare }}>
      {children}
    </CompareContext.Provider>
  );
};

// Admin Provider
const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [adminLoading, setAdminLoading] = useState(true);
  const [adminToken, setAdminToken] = useState(localStorage.getItem("adminToken"));

  const checkAdmin = useCallback(async () => {
    if (!adminToken) {
      setAdminLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setAdmin(response.data);
    } catch (error) {
      setAdmin(null);
      localStorage.removeItem("adminToken");
      setAdminToken(null);
    } finally {
      setAdminLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    checkAdmin();
  }, [checkAdmin]);

  const adminLogin = async (email, password) => {
    const response = await axios.post(`${API}/admin/login`, { email, password });
    const { token, admin: adminData } = response.data;
    localStorage.setItem("adminToken", token);
    setAdminToken(token);
    setAdmin(adminData);
    return adminData;
  };

  const adminLogout = () => {
    localStorage.removeItem("adminToken");
    setAdminToken(null);
    setAdmin(null);
  };

  return (
    <AdminContext.Provider value={{ admin, adminLoading, adminLogin, adminLogout, adminToken }}>
      {children}
    </AdminContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];

      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        setUser(response.data);
        toast.success("Welcome back!");
        navigate("/dashboard", { replace: true, state: { user: response.data } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed");
        navigate("/login");
      }
    };

    processSession();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-stone-600 font-sans">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// Protected Admin Route
const ProtectedAdminRoute = ({ children }) => {
  const { admin, adminLoading } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!adminLoading && !admin) {
      navigate("/admin", { state: { from: location.pathname } });
    }
  }, [admin, adminLoading, navigate, location]);

  if (adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!admin) {
    return null;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Check if admin routes
  const isAdminRoute = location.pathname.startsWith("/admin");

  if (isAdminRoute) {
    return (
      <Routes>
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedAdminRoute>
              <AdminDashboard />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/properties"
          element={
            <ProtectedAdminRoute>
              <AdminProperties />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/properties/new"
          element={
            <ProtectedAdminRoute>
              <AdminPropertyForm />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/properties/:propertyId/edit"
          element={
            <ProtectedAdminRoute>
              <AdminPropertyForm />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/inquiries"
          element={
            <ProtectedAdminRoute>
              <AdminInquiries />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/blog"
          element={
            <ProtectedAdminRoute>
              <AdminBlogList />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/blog/new"
          element={
            <ProtectedAdminRoute>
              <AdminBlogForm />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/blog/edit/:articleId"
          element={
            <ProtectedAdminRoute>
              <AdminBlogForm />
            </ProtectedAdminRoute>
          }
        />
      </Routes>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/property/:propertyId" element={<PropertyDetailPage />} />
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/city/:citySlug" element={<CityPage />} />
          <Route path="/emi-calculator" element={<EMICalculatorPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function App() {
  // Seed database on first load
  useEffect(() => {
    const seedDb = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        // Ignore - likely already seeded
      }
    };
    seedDb();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CompareProvider>
          <AdminProvider>
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  fontFamily: "Manrope, sans-serif",
                },
              }}
            />
            <AppRouter />
          </AdminProvider>
        </CompareProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
